import { describeKnowledgeApiError } from 'utils/knowledgeErrors';
import { useState, useCallback, useRef } from 'react';
import { Form, FormInstance, Input, message, Select } from 'antd';
import { JustRightHeightModal } from 'components/styled/scoped';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { useSpuLabelAll, useSpuLabelMutate } from './useSpuLabel';

type ImplementProps = {
  form: FormInstance;
};

function Implement(props: ImplementProps) {
  const { form } = props;
  const { t } = useTranslate();
  const { data, loading } = useSpuLabelAll();
  const [newMode, setNewMode] = useState(false);

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={(_, values) => {
        if (values.tagId?.value === 'add-new') {
          setNewMode(true);
        } else {
          setNewMode(false);
        }
      }}
      validateTrigger={['onBlur', 'onSubmit']}
    >
      <Form.Item
        required
        name="tagId"
        label={t('knowledge.商品标签')}
        rules={[{ required: true, message: t('knowledge.item.select.search.label') }]}
      >
        <Select
          showSearch={!newMode}
          placeholder={t('knowledge.item.select.search.label')}
          loading={loading}
          optionFilterProp="children"
          labelInValue
          filterOption={(input, option) => {
            return (option?.children as unknown as string)?.indexOf(input.toLowerCase()) >= 0;
          }}
        >
          <Select.Option key="add-new" value="add-new">
            + {t('knowledge.新增x', { x: t('knowledge.商品标签') })}
          </Select.Option>
          {data?.list?.map((item) => (
            <Select.Option key={item.id} value={item.id}>
              {item.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {newMode && (
        <Form.Item
          required
          name="tagName"
          label={t('knowledge.labelTable.row.name')}
          rules={[
            { required: true, message: t('knowledge.label.input.placeholder') },
            {
              validateTrigger: ['onBlur', 'onSubmit'],
              validator: (_, value) => {
                if (data?.list?.filter((item) => item.name === value).length) {
                  return Promise.reject(new Error(t('knowledge.label.name.exist')));
                } else {
                  return Promise.resolve();
                }
              },
            },
          ]}
        >
          <Input placeholder={t('knowledge.label.input.placeholder')} maxLength={60} showCount />
        </Form.Item>
      )}
    </Form>
  );
}

const useQuickAdd = () => {
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const itemsRef = useRef<ItemSelectV2.Item[]>([]);
  const { t } = useTranslate();
  const { createLabel, editLabel } = useSpuLabelMutate();
  const open = useCallback((items: ItemSelectV2.Item[]) => {
    itemsRef.current = items;
    setVisible(true);
  }, []);

  const close = useCallback(() => {
    setVisible(false);
    form.resetFields();
  }, [form]);

  const submit = () => {
    form.validateFields().then(async (values) => {
      try {
        const tagIndexData = itemsRef.current?.map((item) => ({
          spuId: item.externalItemId,
          externalStoreId: item.externalShopId,
          externalType: item.platform,
          region: item.region,
          skuId: item.skuInfo?.map((sku) => sku.skuId),
        }));
        if (values.tagId?.value === 'add-new') {
          await createLabel({
            tagName: values.tagName,
            operateType: 1,
            tagIndexData,
          });
        } else {
          await editLabel({
            tagId: String(values.tagId?.value),
            tagName: values.tagId?.label,
            operateType: 2,
            tagIndexData,
          });
        }
        message.success(t('operation.saveSucceed'));
        close();
      } catch (e) {
        message.error(describeKnowledgeApiError(e, undefined, t('operation.saveFailed')));
      }
    });
  };

  const instance = visible && (
    <JustRightHeightModal
      onCancel={close}
      okText={t('phrase.保存')}
      visible={visible}
      onOk={submit}
      title={t('knowledge.addToLabel')}
      width={414}
    >
      <AlreadySelected>
        {t('knowledge.item.selected.already', { x: itemsRef.current?.length })}
      </AlreadySelected>
      <Implement form={form} />
    </JustRightHeightModal>
  );

  return [open, instance] as const;
};

const AlreadySelected = styled.div`
  background: var(--fett-erp-card-bg);
  border-radius: 4px;
  height: 40px;
  padding: 10px;
  margin-bottom: 16px;
`;

export default useQuickAdd;
